import { Search, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import brazzavilleBridge from '@/assets/brazzaville-bridge-enhanced.jpg';
import { CongoPattern, CongoBorderTop } from '@/components/decorative/CongoPattern';

export function HeroSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/opportunities?search=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <section className="relative section-padding overflow-hidden bg-gradient-to-b from-primary-light/20 via-background to-background">
      {/* Congo Pattern Decorative Layer */}
      <CongoPattern variant="geometric" className="opacity-30" />
      <CongoBorderTop className="absolute top-0 left-0 right-0" />
      
      {/* Brazzaville Bridge Background - Authentic */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/10 to-background/70"></div>
        <img 
          src={brazzavilleBridge} 
          alt="Pont de Brazzaville - République du Congo" 
          className="absolute inset-0 w-full h-full object-cover opacity-50 animate-fade-in"
          style={{ objectPosition: 'center center' }}
          loading="eager"
        />
        
        {/* Refined Gradient Orbs */}
        <div className="absolute top-20 left-10 w-[600px] h-[600px] bg-primary/8 rounded-full blur-[140px] animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-[700px] h-[700px] bg-secondary/8 rounded-full blur-[160px] animate-pulse animation-delay-300"></div>
      </div>
      
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-display md:text-[6rem] lg:text-[8rem] font-extrabold mb-8 animate-fade-in leading-none font-lexend bg-gradient-to-r from-blue-900 via-blue-700 to-blue-900 bg-clip-text text-transparent drop-shadow-lg">
            Mbote!
          </h1>
          <p className="text-3xl md:text-5xl mb-12 animate-fade-in-up animation-delay-200 font-semibold font-ubuntu text-slate-800 drop-shadow-md">
            Boostez votre <span className="bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600 bg-clip-text text-transparent animate-scale-in animation-delay-300 inline-block">carrière</span> à Brazzaville
          </p>
          <p className="text-xl md:text-3xl mb-16 animate-fade-in-up animation-delay-300 font-light font-lexend leading-relaxed max-w-4xl mx-auto text-slate-700 drop-shadow">
            Des milliers d&apos;opportunités : emplois, stages, bourses et formations pour les Congolais
          </p>
          
          {/* Award-Winning Search Bar */}
          <form onSubmit={handleSearch} className="max-w-4xl mx-auto mb-20 animate-scale-in animation-delay-400">
            <div className="flex flex-col md:flex-row gap-4 glass p-4 rounded-3xl border border-border/50 hover:shadow-2xl transition-all duration-500 hover:scale-[1.03] hover:-translate-y-1">
              <div className="relative flex-1">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground transition-transform group-hover:scale-110" />
                <Input
                  type="text"
                  placeholder="Rechercher une opportunité (emploi, stage, bourse...)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-16 h-16 text-lg border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-primary/20 placeholder:text-muted-foreground/60"
                />
              </div>
              <Button type="submit" size="lg" className="h-16 px-12 text-base group shadow-lg hover:shadow-2xl hover:scale-110 transition-all duration-300">
                <span className="font-bold">Rechercher</span>
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
              </Button>
            </div>
          </form>
          
          {/* Award-Winning Stats Grid - Na Lingala */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 max-w-6xl mx-auto">
            <div className="group glass rounded-3xl p-10 border border-border/50 hover:border-primary/60 hover-lift hover-glow transition-all duration-500 animate-fade-in-up animation-delay-500 hover:rotate-2">
              <p className="text-6xl md:text-7xl font-extrabold bg-gradient-to-r from-primary via-primary-dark to-primary bg-clip-text text-transparent mb-3 leading-none group-hover:scale-110 transition-transform">500+</p>
              <p className="text-sm md:text-base text-muted-foreground font-medium tracking-wide">Opportunités - Makasi!</p>
            </div>
            <div className="group glass rounded-3xl p-10 border border-border/50 hover:border-secondary/60 hover-lift hover-glow transition-all duration-500 animate-fade-in-up animation-delay-600 hover:-rotate-2">
              <p className="text-6xl md:text-7xl font-extrabold bg-gradient-to-r from-secondary via-secondary to-secondary/80 bg-clip-text text-transparent mb-3 leading-none group-hover:scale-110 transition-transform">100+</p>
              <p className="text-sm md:text-base text-muted-foreground font-medium tracking-wide">Entreprises - Malamu!</p>
            </div>
            <div className="group glass rounded-3xl p-10 border border-border/50 hover:border-primary/60 hover-lift hover-glow transition-all duration-500 animate-fade-in-up animation-delay-700 hover:rotate-2">
              <p className="text-6xl md:text-7xl font-extrabold bg-gradient-to-r from-primary via-success to-primary-dark bg-clip-text text-transparent mb-3 leading-none group-hover:scale-110 transition-transform">1000+</p>
              <p className="text-sm md:text-base text-muted-foreground font-medium tracking-wide">Jeunes - Biso!</p>
            </div>
            <div className="group glass rounded-3xl p-10 border border-border/50 hover:border-accent/60 hover-lift hover-glow transition-all duration-500 animate-fade-in-up animation-delay-800 hover:-rotate-2">
              <p className="text-6xl md:text-7xl font-extrabold bg-gradient-to-r from-accent via-accent to-accent/80 bg-clip-text text-transparent mb-3 leading-none group-hover:scale-110 transition-transform">50+</p>
              <p className="text-sm md:text-base text-muted-foreground font-medium tracking-wide">Nouveautés - Esengo!</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}